// Curated filter fields per source object type (DEV-4842). These power the
// FilterBuilder's field <select>; operators pick a human label and get the dot
// path into the PipelineEvent. Unknown object types fall back to the generic set
// plus a free-text path escape hatch in the UI.

export interface FilterField {
  label: string;
  path: string;
}

// Present on every object type — reachable regardless of payload shape.
const GENERIC_FIELDS: FilterField[] = [
  { label: "Object type", path: "objectType" },
  { label: "Operation", path: "operation" },
];

const FIELDS_BY_OBJECT_TYPE: Record<string, FilterField[]> = {
  "jump.order": [
    { label: "Order status", path: "payload.orderStatus" },
    { label: "Offer id", path: "payload.offerId" },
    { label: "Market", path: "payload.market" },
    { label: "Order id", path: "payload.orderId" },
    { label: "Fan id", path: "payload.fanId" },
  ],
  "jump.order_item": [
    { label: "Event id", path: "payload.eventId" },
    { label: "Offer id", path: "payload.offerId" },
    { label: "Item type", path: "payload.itemType" },
    { label: "Cancelled", path: "payload.cancelled" },
    { label: "Status", path: "payload.status" },
    { label: "Market", path: "payload.market" },
  ],
  "jump.fan": [
    { label: "Email", path: "payload.email" },
    { label: "Fan id", path: "payload.fanId" },
  ],
};

// Post-transform (source="output") fields — paths into the transform's dest
// payload, keyed by the DEST object type the transform emits. These are the
// enriched fields that don't exist on the raw event (e.g. jump_offer_name,
// resolved via Snowflake), which is the whole reason to filter on the output.
const OUTPUT_FIELDS_BY_DEST_TYPE: Record<string, FilterField[]> = {
  "hubspot.deal": [
    { label: "Offer name (enriched)", path: "properties.jump_offer_name" },
    { label: "Event name (enriched)", path: "properties.jump_event_name" },
    { label: "Deal name", path: "properties.dealname" },
    { label: "Deal stage", path: "properties.dealstage" },
    { label: "Order status", path: "properties.jump_order_status" },
  ],
  "hubspot.line_item": [
    { label: "Offer name (enriched)", path: "properties.jump_offer_name" },
    { label: "Event name (enriched)", path: "properties.jump_event_name" },
    { label: "Line item name", path: "properties.name" },
    { label: "Item status", path: "properties.jump_item_status" },
  ],
  "hubspot.contact": [
    { label: "Email", path: "properties.email" },
    { label: "First name", path: "properties.firstname" },
    { label: "Last name", path: "properties.lastname" },
  ],
};

/**
 * Curated field list for a source object type, always including the generic
 * fields. Unknown / undefined object type → just the generic set (the UI still
 * offers free-text paths).
 */
export function filterFieldsForObjectType(objectType?: string): FilterField[] {
  const specific = objectType ? FIELDS_BY_OBJECT_TYPE[objectType] ?? [] : [];
  return [...specific, ...GENERIC_FIELDS];
}

/**
 * Curated field list for the transform OUTPUT (source="output"), keyed by the
 * transform's DEST object type. Unknown / undefined → empty (the UI still offers
 * free-text paths like `properties.jump_offer_name`).
 */
export function filterFieldsForOutput(destObjectType?: string): FilterField[] {
  return destObjectType ? OUTPUT_FIELDS_BY_DEST_TYPE[destObjectType] ?? [] : [];
}
